import * as React from 'react'
// components
import Grid from '@mui/material/Grid2'
import { AvatarsList, Button, Menu } from 'components/UI'
// context
import { ReviewDetailsBLContext } from 'containers/Review/ReviewDetails/layers/BL'
import { ReviewsListBLContext } from 'containers/Review/ReviewList/layers/BL'
// views
import {
  Chip,
  CardText,
  DotsIcon,
  CardHeader,
  CardWrapper,
  CardInnerWrapper,
  ViewButtonWrapper
} from 'components/Modals/PlanItems/components/ReviewsList/views'
import { AvatarWrapper } from 'components/Modals/PlanItems/components/CompleteReview/views'
// types
import { ReviewStatus } from 'types/graphql'
// themes
import { colors } from 'themes'
// icons
import EditIcon from '@mui/icons-material/EditOutlined'
import WatchIcon from '@mui/icons-material/WatchLaterOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutlineOutlined'
// utils
import moment from 'moment'

const ReviewCard: React.FC = () => {
  const [isHovered, setHovered] = React.useState(false)

  const { review, isCurrentUserReviewer, isDueToday, isOverdue, isDueTomorrow, reviewers, reviewStatus } =
    React.useContext(ReviewDetailsBLContext)

  const {
    entity,
    isSidebar,
    canDelete,
    canModify,
    handleStartReview,
    handleDeleteReview,
    handleShowReviewDetails,
    handleEditScheduledReview
  } = React.useContext(ReviewsListBLContext)

  const isReviewComplete = reviewStatus === ReviewStatus.Complete

  const canModifyReview = canModify && entity.scheduledReviews.some(({ id }) => id === review.scheduledReview.id)

  const onMouseEnter = () => {
    if (reviewStatus !== ReviewStatus.NotStarted) {
      setHovered(true)
    }
  }

  const onMouseLeave = () => {
    if (reviewStatus !== ReviewStatus.NotStarted) {
      setHovered(false)
    }
  }

  const showReviewResult = () => handleShowReviewDetails(review)

  const startReview = () => handleStartReview(review)

  const onEditButtonClick = () => {
    handleEditScheduledReview(review)
  }

  const onDeleteButtonClick = () => {
    handleDeleteReview(review.id)
  }

  const getDueTitle = () => {
    if (isDueToday) {
      return 'Due Today'
    }
    if (isOverdue) {
      return 'Overdue'
    }
    if (isDueTomorrow) {
      return 'Due Tomorrow'
    }
    return `Due in ${review.dueDate.diff(moment().startOf('day'), 'days')} days`
  }

  return (
    <CardWrapper>
      <CardHeader backgroundColor={colors.darkBlue500} reviewStatus={reviewStatus}>{`${review.startPeriod.format(
        'D MMM Y'
      )} to ${review.endPeriod.format('D MMM Y')}`}</CardHeader>
      <CardInnerWrapper
        container
        direction={'column'}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        justifyContent={'space-between'}
        isReviewComplete={isReviewComplete}>
        <Grid container justifyContent={'space-between'}>
          <Grid container direction={'column'} justifyContent={'space-around'}>
            {!isReviewComplete && <CardText>{getDueTitle()}</CardText>}
            <span>{reviewStatus.replace('_', ' ')}</span>
          </Grid>

          {!isReviewComplete && isCurrentUserReviewer && (
            <Grid container alignItems={'baseline'} justifyContent={'flex-end'}>
              <Button
                onClick={startReview}
                style={{ zIndex: 8 }}
                disabled={!moment().isSameOrAfter(review.startPeriod, 'day')}>
                {reviewStatus === ReviewStatus.InProgress ? 'Finish Review' : 'Start Review'}
              </Button>
            </Grid>
          )}
        </Grid>

        <Grid container alignItems={'flex-end'}>
          <Chip
            isDueLate={isOverdue}
            isDueToday={isDueToday}
            isReviewComplete={isReviewComplete}
            label={review.dueDate.format('D MMMM')}
            icon={<WatchIcon style={{ color: colors.white }} />}
          />

          <AvatarWrapper container>
            <AvatarsList list={reviewers} limit={isSidebar ? 6 : 11} />
          </AvatarWrapper>

          {((canDelete && reviewStatus !== ReviewStatus.NotStarted) || canModify) && (
            <Menu trigger={<DotsIcon htmlColor='black' />}>
              {canModifyReview && <span onClick={onEditButtonClick}><EditIcon />Edit Schedule</span>}
              {canDelete && reviewStatus !== ReviewStatus.NotStarted && (
                <span onClick={onDeleteButtonClick}><DeleteIcon />Delete</span>
              )}
            </Menu>
          )}
        </Grid>

        {isHovered && reviewStatus !== ReviewStatus.NotStarted && (
          <ViewButtonWrapper>
            <Button shadowLess onClick={showReviewResult}>
              View
            </Button>
          </ViewButtonWrapper>
        )}
      </CardInnerWrapper>
    </CardWrapper>
  )
}

export default ReviewCard
